import axios from 'axios';

// Configuración base
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Crear instancia de axios específica para usuarios admin
const api = axios.create({
  baseURL: '/api/usuarios-admin',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para agregar token de autorización
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('horeb_admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejo global de errores
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Manejar errores específicos
    if (error.response?.status === 401) {
      // Token expirado, redirigir al login
      localStorage.removeItem('horeb_admin_token');
      localStorage.removeItem('horeb_admin_user');
      window.location.href = '/login';
    }
    
    if (error.response?.status === 403) {
      console.error('Permisos insuficientes');
    }
    
    if (error.response?.status >= 500) {
      console.error('Error del servidor:', error.response.data?.error);
    }
    
    return Promise.reject(error);
  }
);

const usuariosAdminService = {
  /**
   * Obtener lista de usuarios administradores
   * @param {Object} params - Parámetros de consulta
   * @param {number} params.page - Página actual
   * @param {number} params.limit - Cantidad por página
   * @param {string} params.search - Término de búsqueda
   * @param {string} params.rol - Filtro por rol
   * @param {string} params.activo - Filtro por estado activo
   * @returns {Promise<Object>} Lista de usuarios con paginación
   */
  obtenerUsuarios: async (params = {}) => {
    try {
      const response = await api.get('/', { params });
      return response;
    } catch (error) {
      console.error('Error obteniendo usuarios:', error);
      throw error;
    }
  },

  /**
   * Obtener un usuario específico por ID
   * @param {number} id - ID del usuario
   * @returns {Promise<Object>} Datos del usuario
   */
  obtenerUsuario: async (id) => {
    try {
      const response = await api.get(`/${id}`);
      return response;
    } catch (error) {
      console.error('Error obteniendo usuario:', error);
      throw error;
    }
  },

  /**
   * Crear un nuevo usuario administrador
   * @param {Object} userData - Datos del usuario
   * @param {string} userData.usuario - Nombre de usuario
   * @param {string} userData.nombre_persona - Nombre completo
   * @param {string} userData.email - Email
   * @param {string} userData.password - Contraseña
   * @param {string} userData.rol - Rol del usuario
   * @param {Object} userData.permisos_json - Permisos específicos
   * @param {boolean} userData.activo - Estado activo
   * @returns {Promise<Object>} Usuario creado
   */
  crearUsuario: async (userData) => {
    try {
      const response = await api.post('/', userData);
      return response;
    } catch (error) {
      console.error('Error creando usuario:', error);
      throw error;
    }
  },

  /**
   * Actualizar un usuario existente
   * @param {number} id - ID del usuario
   * @param {Object} userData - Datos actualizados del usuario
   * @returns {Promise<Object>} Usuario actualizado
   */
  actualizarUsuario: async (id, userData) => {
    try {
      const response = await api.put(`/${id}`, userData);
      return response;
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      throw error;
    }
  },

  /**
   * Eliminar (desactivar) un usuario
   * @param {number} id - ID del usuario
   * @returns {Promise<Object>} Resultado de la operación
   */
  eliminarUsuario: async (id) => {
    try {
      const response = await api.delete(`/${id}`);
      return response;
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      throw error;
    }
  },

  /**
   * Resetear contraseña de un usuario
   * @param {number} id - ID del usuario
   * @param {string} nueva_password - Nueva contraseña temporal
   * @returns {Promise<Object>} Resultado de la operación
   */
  resetearPassword: async (id, nueva_password) => {
    try {
      const response = await api.post(`/${id}/resetear-password`, {
        nueva_password
      });
      return response;
    } catch (error) {
      console.error('Error reseteando contraseña:', error);
      throw error;
    }
  },

  /**
   * Obtener registros de auditoría
   * @param {Object} params - Parámetros de consulta
   * @param {number} params.page - Página actual
   * @param {number} params.limit - Cantidad por página
   * @param {string} params.usuario_id - Filtro por usuario
   * @param {string} params.accion - Filtro por acción
   * @param {string} params.fecha_desde - Fecha desde
   * @param {string} params.fecha_hasta - Fecha hasta
   * @returns {Promise<Object>} Registros de auditoría con paginación
   */
  obtenerAuditoria: async (params = {}) => {
    try {
      const response = await api.get('/auditoria', { params });
      return response;
    } catch (error) {
      console.error('Error obteniendo auditoría:', error);
      throw error;
    }
  },

  /**
   * Obtener sesiones activas
   * @returns {Promise<Object>} Lista de sesiones activas
   */
  obtenerSesionesActivas: async () => {
    try {
      const response = await api.get('/sesiones-activas');
      return response;
    } catch (error) {
      console.error('Error obteniendo sesiones activas:', error);
      throw error;
    }
  },

  /**
   * Cerrar una sesión específica
   * @param {number} sesionId - ID de la sesión
   * @returns {Promise<Object>} Resultado de la operación
   */
  cerrarSesion: async (sesionId) => {
    try {
      const response = await api.post(`/sesiones/${sesionId}/cerrar`);
      return response;
    } catch (error) {
      console.error('Error cerrando sesión:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas de usuarios
   * @returns {Promise<Object>} Estadísticas generales
   */
  obtenerEstadisticas: async () => {
    try {
      // Esta función puede obtener estadísticas desde múltiples endpoints
      const [usuarios, sesiones] = await Promise.all([
        api.get('/?limit=1'), // Solo para obtener el total
        api.get('/sesiones-activas')
      ]);

      return {
        total_usuarios: usuarios.pagination?.total || 0,
        sesiones_activas: sesiones.data?.length || 0
      };
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      throw error;
    }
  },

  /**
   * Validar disponibilidad de usuario/email
   * @param {string} campo - 'usuario' o 'email'
   * @param {string} valor - Valor a validar
   * @param {number} excludeId - ID a excluir de la validación (para edición)
   * @returns {Promise<boolean>} true si está disponible
   */
  validarDisponibilidad: async (campo, valor, excludeId = null) => {
    try {
      const params = {
        [campo]: valor
      };
      
      if (excludeId) {
        params.exclude_id = excludeId;
      }

      const response = await api.get('/validar-disponibilidad', { params });
      return response.disponible;
    } catch (error) {
      console.error('Error validando disponibilidad:', error);
      return false;
    }
  },

  /**
   * Obtener configuración de roles y permisos
   * @returns {Promise<Object>} Configuración de roles
   */
  obtenerConfiguracionRoles: async () => {
    try {
      // Esta podría ser una configuración local o venir del servidor
      return {
        roles: {
          super_admin: {
            nombre: 'Super Administrador',
            descripcion: 'Acceso completo al sistema',
            permisos_defecto: {
              conjuntos: { crear: true, editar: true, eliminar: true, ver: true },
              usuarios: { crear: true, editar: true, eliminar: true, ver: true },
              metricas: { ver: true },
              auditoria: { ver: true }
            }
          },
          admin_conjuntos: {
            nombre: 'Administrador de Conjuntos',
            descripcion: 'Gestión de conjuntos residenciales',
            permisos_defecto: {
              conjuntos: { crear: true, editar: true, eliminar: false, ver: true },
              usuarios: { crear: false, editar: false, eliminar: false, ver: true },
              metricas: { ver: true },
              auditoria: { ver: false }
            }
          },
          admin_lectura: {
            nombre: 'Administrador de Solo Lectura',
            descripcion: 'Acceso de solo lectura',
            permisos_defecto: {
              conjuntos: { crear: false, editar: false, eliminar: false, ver: true },
              usuarios: { crear: false, editar: false, eliminar: false, ver: false },
              metricas: { ver: true },
              auditoria: { ver: false }
            }
          }
        }
      };
    } catch (error) {
      console.error('Error obteniendo configuración de roles:', error);
      throw error;
    }
  }
};

export default usuariosAdminService;